import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import AutoComplete from '../components/AutoComplete';
import { trabajadorService, tablaService } from '../services/api';
import '../styles/datatable.css';
import { getErrorDetail } from '../utils/errorHelper';

const TIPOS_DOC = ['DNI', 'CE', 'Pasaporte'];

const Trabajadores = () => {
  const [records, setRecords] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  // Modal "Crear cargo nuevo" desde el AutoComplete
  const [modalCargo, setModalCargo] = useState(false);
  const [savingCargo, setSavingCargo] = useState(false);
  const [formCargo, setFormCargo] = useState({ codigo: '', descripcion: '' });

  const emptyForm = { dni: '', nombres: '', id_cargo: null, edad: '', sexo: '', telefono: '', correo: '', email: '', tipo_documento: 'DNI', es_chofer: false, licencia: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [trab, carg] = await Promise.all([
        trabajadorService.getAll(),
        tablaService('cargos').getAll(),
      ]);
      setRecords(trab.data);
      setCargos((carg.data || []).map(c => ({ ...c, display_cargo: `${c.codigo} — ${c.descripcion}` })));
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar trabajadores.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const truthy = (v) => {
      if (v === undefined || v === null) return false;
      const s = String(v).trim().toUpperCase();
      return s === 'S' || s === 'SI' || s === 'SÍ' || s === 'X' || s === 'TRUE' || s === '1';
    };
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      const cargosByCodigo = Object.fromEntries(cargos.map(c => [String(c.codigo).toUpperCase(), c.id_cargo]));
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const nombres = row['Nombres'] || row['nombres'] || '';
        let errorMsg = null;
        if (!nombres) {
          fail++; errorMsg = `Fila ${i + 1}: Nombres vacio (obligatorio)`;
        } else {
          // Resolver cargo por código (opcional). Si viene un código que no existe -> error.
          let id_cargo = null;
          const codCargo = String(row['Codigo Cargo'] || row['codigo_cargo'] || '').trim().toUpperCase();
          if (codCargo) {
            id_cargo = cargosByCodigo[codCargo];
            if (!id_cargo) {
              fail++;
              errorMsg = `Fila ${i + 1} ("${nombres}"): Codigo de cargo "${codCargo}" no existe en tabla Cargos`;
              if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
              continue;
            }
          }
          const esChofer = truthy(row['Es Chofer'] || row['es_chofer']);
          const licencia = String(row['Licencia'] || row['licencia'] || '').trim() || null;
          if (esChofer && !licencia) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${nombres}"): Es Chofer requiere Licencia`;
            if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
            continue;
          }
          try {
            await trabajadorService.create({
              dni: row['DNI'] || row['dni'] || null,
              nombres,
              id_cargo,
              edad: row['Edad'] || row['edad'] || null,
              sexo: row['Sexo'] || row['sexo'] || null,
              telefono: row['Telefono'] || row['telefono'] || null,
              correo: row['Correo'] || row['correo'] || null,
              email: row['Email'] || row['email'] || null,
              tipo_documento: row['Tipo Doc.'] || row['tipo_documento'] || null,
              es_chofer: esChofer,
              licencia,
            });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${nombres}"): ${getErrorDetail(err, 'Error al crear trabajador')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, [cargos, fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      dni: r.dni || '', nombres: r.nombres || '', id_cargo: r.id_cargo || null, edad: r.edad || '',
      sexo: r.sexo || '', telefono: r.telefono || '', correo: r.correo || '',
      email: r.email || '', tipo_documento: r.tipo_documento || '',
      es_chofer: !!r.es_chofer, licencia: r.licencia || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombres) { setAlert({ type: 'warning', message: 'Nombres es obligatorio.' }); return; }
    if (form.es_chofer && !form.licencia.trim()) {
      setAlert({ type: 'warning', message: 'La licencia es obligatoria cuando el trabajador es chofer.' });
      return;
    }
    const data = {};
    Object.entries(form).forEach(([k, v]) => {
      if (k === 'edad') data[k] = v === '' ? null : parseInt(v) || null;
      else if (k === 'id_cargo') data[k] = v ? parseInt(v) : null;
      else if (k === 'es_chofer') data[k] = !!v;
      else data[k] = v === '' ? null : v;
    });
    try {
      if (editing) await trabajadorService.update(editing.id_trabajador, data);
      else await trabajadorService.create(data);
      setShowModal(false); fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'Error al guardar.' });
    }
  };

  // Crear cargo nuevo desde el AutoComplete (modal pequeño)
  const abrirNuevoCargo = () => {
    setFormCargo({ codigo: '', descripcion: '' });
    setModalCargo(true);
  };
  const handleSaveCargo = async (e) => {
    e.preventDefault();
    if (!formCargo.codigo.trim() || !formCargo.descripcion.trim()) {
      setAlert({ type: 'warning', message: 'Código y descripción del cargo son obligatorios.' });
      return;
    }
    setSavingCargo(true);
    try {
      const res = await tablaService('cargos').create({
        codigo: formCargo.codigo.trim().toUpperCase(),
        descripcion: formCargo.descripcion.trim(),
      });
      const nuevo = res.data;
      const itemUI = { ...nuevo, display_cargo: `${nuevo.codigo} — ${nuevo.descripcion}` };
      setCargos(prev => [...prev, itemUI]);
      setForm(prev => ({ ...prev, id_cargo: nuevo.id_cargo }));
      setModalCargo(false);
      setToast(`Cargo "${nuevo.descripcion}" creado`);
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al crear el cargo.') });
    } finally { setSavingCargo(false); }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Trabajador', message: `¿Eliminar "${r.nombres}"?`,
      onConfirm: async () => { setConfirm(null); try { await trabajadorService.delete(r.id_trabajador); setToast('Trabajador eliminado'); fetchData(); } catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); } },
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'es_chofer' && !checked ? { licencia: '' } : {}),
    }));
  };

  const columns = [
    { key: 'dni', label: 'DNI', width: 100, style: { fontWeight: 600 } },
    { key: 'nombres', label: 'Nombres' },
    { key: 'cargo_descripcion', label: 'Cargo', width: 180,
      render: (r) => r.cargo_codigo
        ? <span><span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#93c5fd', marginRight: 6 }}>{r.cargo_codigo}</span>{r.cargo_descripcion}</span>
        : <span style={{ color: '#64748b' }}>—</span> },
    { key: 'es_chofer', label: 'Chofer', width: 70, style: { textAlign: 'center' }, render: (r) => r.es_chofer ? '✓' : '' },
    { key: 'licencia', label: 'Licencia', width: 110 },
    { key: 'telefono', label: 'Telefono', width: 120 },
  ];

  const importCols = [
    { key: 'Tipo Doc.', label: 'Tipo Doc.', required: false, example: 'DNI', description: 'Tipo de documento: DNI, CE, Pasaporte' },
    { key: 'DNI', label: 'DNI', required: false, example: '12345678', description: 'Numero de documento de identidad (no se permiten DNIs duplicados)' },
    { key: 'Nombres', label: 'Nombres', required: true, example: 'Juan Carlos Perez Lopez', description: 'Nombres completos del trabajador' },
    { key: 'Codigo Cargo', label: 'Codigo Cargo', required: false, example: 'JEFE', description: 'Codigo del cargo (Utilitarios > Cargos). Si no existe la fila falla.' },
    { key: 'Edad', label: 'Edad', required: false, example: '30', description: 'Edad en anos (numero entero)' },
    { key: 'Sexo', label: 'Sexo', required: false, example: 'M', description: 'Sexo: M=Masculino, F=Femenino' },
    { key: 'Telefono', label: 'Telefono', required: false, example: '987654321', description: 'Numero de telefono' },
    { key: 'Correo', label: 'Correo', required: false, example: 'juan@empresa.com', description: 'Correo electronico' },
    { key: 'Email', label: 'Email', required: false, example: 'juan.personal@gmail.com', description: 'Email alternativo' },
    { key: 'Es Chofer', label: 'Es Chofer', required: false, example: 'S', description: 'S/N o 1/0 (default N)' },
    { key: 'Licencia', label: 'Licencia', required: false, example: 'Q12345678', description: 'Nro de licencia (obligatorio si Es Chofer = S)' },
  ];

  return (
    <Layout title="Trabajadores" subtitle="Entidades">
      <div className="page-header">
        <h2>Trabajadores</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
      </div>

      <DataTable columns={columns} data={records} idField="id_trabajador" onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay trabajadores registrados" exportFileName="trabajadores" importColumns={importCols} />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:540}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <h2>{editing ? 'Editar Trabajador' : 'Trabajador'}</h2>
                {!editing && <span className="modal-header-badge"><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg></span>}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px'}}>
                  <div className="form-row">
                    <label>Tipo Documento</label>
                    <select name="tipo_documento" value={form.tipo_documento} onChange={handleChange}>
                      <option value="">-- Seleccionar --</option>
                      {TIPOS_DOC.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-row"><label>DNI / Documento</label><input name="dni" value={form.dni} onChange={handleChange} placeholder="Nro. documento" /></div>
                </div>
                <div className="form-row"><label>Nombres *</label><input name="nombres" value={form.nombres} onChange={handleChange} placeholder="Nombres completos" autoFocus /></div>
                <AutoComplete
                  label="Cargo"
                  value={form.id_cargo}
                  options={cargos}
                  idField="id_cargo"
                  displayField="display_cargo"
                  codeField="codigo"
                  onChange={(v) => setForm(prev => ({ ...prev, id_cargo: v || null }))}
                  onAdd={abrirNuevoCargo}
                  addLabel="Cargo"
                  placeholder="Buscar cargo..."
                />
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0 16px'}}>
                  <div className="form-row"><label>Edad</label><input name="edad" type="number" value={form.edad} onChange={handleChange} placeholder="Edad" /></div>
                  <div className="form-row">
                    <label>Sexo</label>
                    <select name="sexo" value={form.sexo} onChange={handleChange}>
                      <option value="">-- Seleccionar --</option>
                      <option value="M">Masculino</option>
                      <option value="F">Femenino</option>
                    </select>
                  </div>
                  <div className="form-row"><label>Telefono</label><input name="telefono" value={form.telefono} onChange={handleChange} placeholder="Telefono" /></div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px'}}>
                  <div className="form-row"><label>Correo</label><input name="correo" value={form.correo} onChange={handleChange} placeholder="correo@ejemplo.com" /></div>
                  <div className="form-row"><label>Email</label><input name="email" value={form.email} onChange={handleChange} placeholder="email@ejemplo.com" /></div>
                </div>

                {/* ── Chofer ── */}
                <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 14 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none', width: 'fit-content' }}>
                    <input
                      type="checkbox"
                      name="es_chofer"
                      checked={form.es_chofer}
                      onChange={handleChange}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }}
                    />
                    <span style={{ fontWeight: 600, fontSize: 14 }}>Es chofer</span>
                  </label>

                  {form.es_chofer && (
                    <div className="form-row" style={{ marginTop: 10 }}>
                      <label>
                        Licencia de Conducir *
                        <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--text-muted)', marginLeft: 4 }}>(obligatoria)</span>
                      </label>
                      <input
                        name="licencia"
                        value={form.licencia}
                        onChange={handleChange}
                        placeholder="Nro. de licencia"
                        autoFocus
                        style={{ fontFamily: 'monospace', letterSpacing: 1 }}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Modal Crear Cargo (desde el AutoComplete) ─── */}
      {modalCargo && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nuevo Cargo</h2>
              <button className="modal-close" onClick={() => setModalCargo(false)}>×</button>
            </div>
            <form onSubmit={handleSaveCargo}>
              <div className="modal-body">
                <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0 14px' }}>
                  <div className="form-row">
                    <label>Código *</label>
                    <input value={formCargo.codigo}
                      onChange={e => setFormCargo(p => ({ ...p, codigo: e.target.value.toUpperCase() }))}
                      placeholder="Ej: JEFE" maxLength={20} autoFocus
                      style={{ fontFamily: 'monospace', letterSpacing: 1, fontWeight: 600 }} />
                  </div>
                  <div className="form-row">
                    <label>Descripción *</label>
                    <input value={formCargo.descripcion}
                      onChange={e => setFormCargo(p => ({ ...p, descripcion: e.target.value }))}
                      placeholder="Ej: Jefe de Almacén" maxLength={200} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModalCargo(false)} disabled={savingCargo}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={savingCargo}>
                  {savingCargo ? 'Guardando...' : 'Crear Cargo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default Trabajadores;
